import React, { useEffect } from "react";
import { Controller } from "react-hook-form";
import { CustomIcon } from "../../../UI";
import FileUploadSection from "./FileUploadSection";
import CarrierPeopleSection from "./CarrierPeopleSection";

const CarrierFormFields = ({
  register,
  errors,
  formValues,
  setValue,
  watch,
  control,
  // Equipment handlers
  handleAddEquipmentGroup,
  handleRemoveEquipmentGroup,
  handleEquipmentTypeChange,
  handleAddSize,
  handleRemoveSize,
  handleSizeChange,
  handleCustomSizeChange,
  handleCheckboxChange,
  // File handlers
  handleCarrierFilesChange,
  handleRemoveCarrierFile,
  getFileName,
  carrierFileError,
  // Options
  equipmentTypeOptions,
  sizeOptions,
  capabilitiesOptions,
  certificationsOptions
}) => {
  return (
    <>

      <div className="form-grid">
        <div className="form-field">
          <label htmlFor="companyName" className="form-label">Company Name *</label>
          <input
            id="companyName"
            className={`form-input ${errors.companyName ? "input-error" : ""}`}
            placeholder="Company Name"
            {...register("companyName", { required: "Company Name is required" })}
            aria-invalid={!!errors.companyName}
          />
          {errors.companyName && <p className="error-text">{errors.companyName.message}</p>}
        </div>

        <div className="form-field">
          <label htmlFor="dba" className="form-label">DBA</label>
          <input
            id="dba"
            className={`form-input ${errors.dba ? "input-error" : ""}`}
            placeholder="Doing Business As"
            {...register("dba")}
            aria-invalid={!!errors.dba}
          />
          {errors.dba && <p className="error-text">{errors.dba.message}</p>}
        </div>

        <div className="form-field">
          <label htmlFor="mcNumber" className="form-label">MC# *</label>
          <input
            id="mcNumber"
            className={`form-input ${errors.mcNumber ? "input-error" : ""}`}
            placeholder="MC Number"
            {...register("mcNumber", { required: "MC Number is required" })}
            aria-invalid={!!errors.mcNumber}
          />
          {errors.mcNumber && <p className="error-text">{errors.mcNumber.message}</p>}
        </div>

        <div className="form-field">
          <label htmlFor="dotNumber" className="form-label">DOT# *</label>
          <input
            id="dotNumber"
            className={`form-input ${errors.dotNumber ? "input-error" : ""}`}
            placeholder="DOT Number"
            {...register("dotNumber", { required: "DOT Number is required" })}
            aria-invalid={!!errors.dotNumber}
          />
          {errors.dotNumber && <p className="error-text">{errors.dotNumber.message}</p>}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-field">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            id="email"
            type="email"
            className={`form-input ${errors.email ? "input-error" : ""}`}
            placeholder="email@example.com"
            {...register("email", {
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email format"
              }
            })}
            aria-invalid={!!errors.email}
          />
          {errors.email && <p className="error-text">{errors.email.message}</p>}
        </div>

        <div className="form-field">
          <label htmlFor="message" className="form-label">Phone Number</label>
          <input
            id="message"
            type="tel"
            className={`form-input ${errors.message ? "input-error" : ""}`}
            placeholder="Phone Number"
            {...register("message")}
            aria-invalid={!!errors.message}
          />
          {errors.message && <p className="error-text">{errors.message.message}</p>}
        </div>
      </div>

      <div className="form-section address-section">
        <span className="section-title">Address Information</span>
        <div className="form-grid form-grid--single">
          <div className="form-field">
            <label htmlFor="carrierAddress.address" className="form-label">Address</label>
            <input
              id="carrierAddress.address"
              className="form-input"
              placeholder="Street Address"
              {...register("carrierAddress.address")}
            />
          </div>
        </div>

        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="carrierAddress.city" className="form-label">City *</label>
            <input
              id="carrierAddress.city"
              className={`form-input ${errors.carrierAddress?.city ? "input-error" : ""}`}
              placeholder="City"
              {...register("carrierAddress.city", { required: "City is required" })}
              aria-invalid={!!errors.carrierAddress?.city}
            />
            {errors.carrierAddress?.city && <p className="error-text">{errors.carrierAddress.city.message}</p>}
          </div>

          <div className="form-field">
            <label htmlFor="carrierAddress.state" className="form-label">State *</label>
            <input
              id="carrierAddress.state"
              className={`form-input ${errors.carrierAddress?.state ? "input-error" : ""}`}
              placeholder="State"
              {...register("carrierAddress.state", { required: "State is required" })}
              aria-invalid={!!errors.carrierAddress?.state}
            />
            {errors.carrierAddress?.state && <p className="error-text">{errors.carrierAddress.state.message}</p>}
          </div>

          <div className="form-field">
            <label htmlFor="carrierAddress.zipCode" className="form-label">Zip Code *</label>
            <input
              id="carrierAddress.zipCode"
              className={`form-input ${errors.carrierAddress?.zipCode ? "input-error" : ""}`}
              placeholder="Zip Code"
              {...register("carrierAddress.zipCode", { required: "Zip Code is required" })}
              aria-invalid={!!errors.carrierAddress?.zipCode}
            />
            {errors.carrierAddress?.zipCode && <p className="error-text">{errors.carrierAddress.zipCode.message}</p>}
          </div>
        </div>
      </div>

      <div className="form-section">
        <span className="section-title">Equipment Details</span>
        
        {/* Equipment Types with Sizes */}
        <div className="equipment-section">
          <div className="equipment-section-header">
            <span className="equipment-section-label">Equipment Type</span>
            <button
              type="button"
              className="add-equipment-type-btn"
              onClick={handleAddEquipmentGroup}
              aria-label="Add Another Equipment Type"
            >
              <CustomIcon name="plus-01" size={16} type="svg" />
            </button>
          </div>
          <div className="equipment-items-list">
            {(formValues.equipment || []).map((group, groupIndex) => (
              <div key={`equipmentType-${groupIndex}-${group.type}`} className="equipment-group">
                <div className="form-field equipment-type-field">
                  <div className="equipment-type-input-wrapper">
                    <select
                      id={`equipmentType-${groupIndex}`}
                      className="form-select"
                      value={group.type}
                      onChange={(e) => handleEquipmentTypeChange(groupIndex, e.target.value)}
                      required={groupIndex === 0}
                    >
                      <option value="">Select Equipment Type {groupIndex === 0 && '*'}</option>
                      {equipmentTypeOptions
                        .filter(type => !formValues.equipment.some((item, idx) => idx !== groupIndex && item.type === type) || type === group.type)
                        .map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                    </select>
                    {groupIndex > 0 && (
                      <button
                        type="button"
                        className="remove-equipment-type-btn"
                        onClick={() => handleRemoveEquipmentGroup(groupIndex)}
                        aria-label="Remove Equipment Type"
                      >
                        <CustomIcon name="close-small" size={14} type="svg" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="equipment-section equipment-sizes">
         
                  <div className="equipment-items-list">
                    {(group.sizes || []).map((sizeItem, sizeIndex) => (
                      <div key={`size-${groupIndex}-${sizeIndex}`} className="equipment-item-row">
                        <div className="form-field equipment-type-field">
                          <div className="equipment-type-input-wrapper">
                            <select
                              id={`size-${groupIndex}-${sizeIndex}`}
                              className="form-select"
                              value={sizeItem.value}
                              onChange={(e) => handleSizeChange(groupIndex, sizeIndex, e.target.value)}
                            >
                              <option value="">Select Size</option>
                              {sizeOptions.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                            {sizeItem.value === "Other" && (
                              <input
                                type="text"
                                className="form-input"
                                placeholder="Custom size"
                                value={sizeItem.custom}
                                onChange={(e) => handleCustomSizeChange(groupIndex, sizeIndex, e.target.value)}
                              />
                            )}
                            {sizeIndex === 0 ? (
                              <button
                                type="button"
                                className="add-equipment-type-btn add-size-btn"
                                onClick={() => handleAddSize(groupIndex)}
                                aria-label="Add Another Size"
                              >
                                <CustomIcon name="plus-01" size={14} type="svg" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="remove-equipment-type-btn"
                                onClick={() => handleRemoveSize(groupIndex, sizeIndex)}
                                aria-label="Remove Size"
                              >
                                <CustomIcon name="close-small" size={14} type="svg" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="form-section">
        <span className="section-title">Capabilities</span>
        <div className="form-field">
          <div className="checkbox-group">
            <div className="checkbox-row">
              {capabilitiesOptions.map((cap) => (
                <label key={cap} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={(formValues.capabilities || []).includes(cap)}
                    onChange={() => handleCheckboxChange('capabilities', cap)}
                  />
                  <span>{cap}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="form-section">
        <span className="section-title">Certifications</span>
        <div className="form-field">
          <div className="checkbox-group">
            <div className="checkbox-row">
              {certificationsOptions.map((cert) => (
                <label key={cert} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={(formValues.certifications || []).includes(cert)}
                    onChange={() => handleCheckboxChange('certifications', cert)}
                  />
                  <span>{cert}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="form-section">
        <span className="section-title">Banking Information</span>
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="carrierBankAccount" className="form-label">Bank Name</label>
            <input
              id="carrierBankAccount"
              className="form-input"
              placeholder="First National Bank"
              {...register("carrierBankAccount")}
            />
          </div>

          <div className="form-field">
            <label htmlFor="carrierRouting" className="form-label">Routing Number</label>
            <input
              id="carrierRouting"
              className="form-input"
              placeholder="123456789"
              {...register("carrierRouting")}
              maxLength={9}
            />
          </div>

          <div className="form-field">
            <label htmlFor="carrierAccountNumber" className="form-label">Account Number</label>
            <input
              id="carrierAccountNumber"
              className="form-input"
              placeholder="9876543210"
              {...register("carrierAccountNumber")}
            />
          </div>
        </div>
      </div>

      <CarrierPeopleSection
        formValues={formValues}
        setValue={setValue}
      />

      <FileUploadSection
        title="Carrier Documents"
        formValues={formValues}
        fileFieldName="carrierFiles"
        existingFilesFieldName="existingCarrierFiles"
        fileError={carrierFileError}
        onFileChange={handleCarrierFilesChange}
        onRemoveFile={handleRemoveCarrierFile}
        getFileName={getFileName}
      />
    </>
  );
};

export default CarrierFormFields;

